/**
 * Camera-zoom preference. A multiplier applied on top of WorldScene's
 * `computeZoom()` baseline so each player can tune the view to their
 * taste without touching per-map zoom logic.
 *
 * Range: 0.5×..1.5× (default 1.0× — preserves current behavior).
 * Final zoom = round(computeZoom() × multiplier), clamped to ≥1.
 * Rounding to an integer keeps pixel-art rendering crisp; the slider
 * is continuous but final zoom snaps to integers naturally.
 *
 * Persisted to localStorage under its own key (separate from the save
 * snapshot) so it survives Reset Run. Storage failures stay in-memory
 * for the session — same degradation pattern as controlMode.ts.
 *
 * Out of band: per TODOs #28, this is the v0.1 polish slider added
 * after deployed user testing of the seamless world. Future-evolves
 * with the camera system; the multiplier shape is forward-compatible
 * with per-mode (interior vs exterior) ranges if needed.
 */

const STORAGE_KEY = 'poke-camera-zoom';
const DEFAULT_MULTIPLIER = 1.0;
const MIN_MULTIPLIER = 0.5;
const MAX_MULTIPLIER = 1.5;

export const ZOOM_RANGE = {
  min: MIN_MULTIPLIER,
  max: MAX_MULTIPLIER,
  default: DEFAULT_MULTIPLIER,
} as const;

export function clampZoomMultiplier(v: number): number {
  if (!Number.isFinite(v)) return DEFAULT_MULTIPLIER;
  if (v < MIN_MULTIPLIER) return MIN_MULTIPLIER;
  if (v > MAX_MULTIPLIER) return MAX_MULTIPLIER;
  return v;
}

export function getZoomMultiplier(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_MULTIPLIER;
    const parsed = parseFloat(raw);
    return clampZoomMultiplier(parsed);
  } catch {
    return DEFAULT_MULTIPLIER;
  }
}

export function setZoomMultiplier(v: number): void {
  const clamped = clampZoomMultiplier(v);
  try {
    localStorage.setItem(STORAGE_KEY, String(clamped));
  } catch {
    // Silent — in-memory only this session.
  }
}

/** Apply the multiplier to a baseline zoom + round to an integer ≥1.
 *  Pure helper for tests + for WorldScene's computeZoom() consumer. */
export function applyZoomMultiplier(baseline: number, multiplier: number): number {
  const mult = clampZoomMultiplier(multiplier);
  return Math.max(1, Math.round(baseline * mult));
}
